import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output
} from '@angular/core';

const FOOTER_OFFSET = 400;

@Component({
    selector: 'scroll-container',
    templateUrl: './scroll-container.component.html',
    styles: ['.loader-container { height: 50px; width: 50px; margin: 10px auto} ',
        '.loader-text {display: block; text-align: center; margin-bottom: 20px; font-size: 12px}'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScrollContainerComponent implements AfterViewInit, OnDestroy {
    private emittingEvent = false;
    private animationFrameId: number | null = null;
    private resetTimerId: number | null = null;

    private readonly viewportChangeListener = () => {
        if (this.animationFrameId !== null) {
            return;
        }

        // Sample once per frame outside Angular; re-enter only when more content is requested.
        this.animationFrameId = window.requestAnimationFrame(() => {
            this.animationFrameId = null;
            this.checkScroll();
        });
    };

    @Output() endOfContent = new EventEmitter<void>();

    @Input() loading = false;

    @Input() reloading = false;

    @Input() initializing = false;

    @Input() loadingText = 'Loading more...';

    @Input() reloadingText = '';

    constructor(private readonly ngZone: NgZone) {
    }

    ngAfterViewInit(): void {
        this.ngZone.runOutsideAngular(() => {
            window.addEventListener('scroll', this.viewportChangeListener, {passive: true});
            window.addEventListener('touchend', this.viewportChangeListener, {passive: true});
            window.addEventListener('touchcancel', this.viewportChangeListener, {passive: true});
            window.addEventListener('resize', this.viewportChangeListener);
        });
    }

    ngOnDestroy(): void {
        window.removeEventListener('scroll', this.viewportChangeListener);
        window.removeEventListener('touchend', this.viewportChangeListener);
        window.removeEventListener('touchcancel', this.viewportChangeListener);
        window.removeEventListener('resize', this.viewportChangeListener);

        if (this.animationFrameId !== null) {
            window.cancelAnimationFrame(this.animationFrameId);
        }
        if (this.resetTimerId !== null) {
            window.clearTimeout(this.resetTimerId);
        }
    }

    private checkScroll(): void {
        if (this.loading || this.initializing) {
            return;
        }

        if (this.emittingEvent) {
            return;
        }
        const scrollHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
        if ((window.innerHeight + window.scrollY) >= scrollHeight - FOOTER_OFFSET) {
            this.emittingEvent = true;

            this.ngZone.run(() => this.endOfContent.emit());

            // Avoid sending duplicate page requests while the parent updates its loading input.
            this.resetTimerId = window.setTimeout(() => {
                this.emittingEvent = false;
                this.resetTimerId = null;
            }, 1000);
        }
    }
}
